import { type AxiosInstance } from 'axios'

type HistoricalData = {
  company: number
  year: number
  scope_one?: string
  scope_two?: string
  scope_three?: string
  active_toggle?: boolean
}

type HistoricalRevenueData = {
  company: number
  year: number
  revenue?: number
  currency?: string
}
type Source = {
  ids: number[]
}
type SourceUpdatePayload = {
  name: string
  description?: string
  ownership?: number
  group?: number
  category_types?: object
  deleted?: boolean
  archived?: boolean
  labels?: string[]
  ownership_percentage?: string
  location?: string
}
type GroupsPayload = {
  name: string
  description?: string
  id?: number
  ownership?: number[]
}
type ActivitiesPayload = {
  name: string
  description?: string
  category?: string
}
type QuestionsPayload = {
  option?: number
  short_code: string | number
  question_title: string
  measurement?: string | number
  activity: string | number
}
type calculatorsPayload = {
  option?: number
  formula_title: string
  formula: string
  pollutant: string
  activity: number | string

}
type MultipleQuestions = {
  question_title: string
  short_code: string
  question_id: string | number
  measurement: string | number
  option: string | number
}
type Multiplecalculator = {
  formula_title: string
  formula: string
  pollutant: string
  id: number | string

}
interface SourceData {
  company: number
  name: string
  description: string | null
  ownership: number | null
  group: number | null
  category_types: object
  deleted?: boolean | true
  archived?: boolean | true
  labels: Array<number> | []
  ownership_percentage: string | null
  location: string | null
}

export default (axios: AxiosInstance) => ({
  multiCreateHistoricalData(createData: Array<HistoricalData>) {
    return axios.post('/v1/emissiontracker/historical-emissions-data/multiple/create/', createData)
  },
  companyHistoricalData(companyId: number | string) {
    return axios.get(
      `/v1/emissiontracker/historical-data/company/${companyId}/`,
    )
  },
  updateCompanyHistoricalData(dataId: number, updateData: HistoricalData) {
    return axios.patch(
      `/v1/emissiontracker/historical-data/${dataId}/`,
      updateData,
    )
  },
  deleteCompanyHistoricalData(dataId: number) {
    return axios.delete(`/v1/emissiontracker/historical-data/${dataId}/`)
  },
  multiCreateHistoricalRevenueData(createData: Array<HistoricalRevenueData>) {
    return axios.post('/v1/emissiontracker/historical-revenue-data/multiple/create/', createData)
  },
  companyHistoricalRevenueData(companyId: number | string) {
    return axios.get(
      `/v1/emissiontracker/historical-revenue-data/company/${companyId}/`,
    )
  },
  deleteHistoricalRevenueData(dataId: number) {
    return axios.delete(`/v1/emissiontracker/historical-revenue-data/${dataId}/`)
  },
  listCategories() {
    return axios.get<EmissionCategory[]>('/v1/emissiontracker/categories/')
  },
  fetchCategory(id: number) {
    return axios.get<EmissionCategory>(`/v1/emissiontracker/categories/${id}/`)
  },
  listTypes() {
    return axios.get('/v1/emissiontracker/activities/get-all/')
  },
  listOwnerships() {
    return axios.get('/v1/emissiontracker/ownerships/')
  },
  listGroups() {
    return axios.get('/v1/emissiontracker/groups/')
  },
  createGroups(selected: GroupsPayload) {
    return axios.post('/v1/emissiontracker/groups/', selected, {
    },
    )
  },
  updateGroups(id: number, selected: GroupsPayload) {
    return axios.put(`/v1/emissiontracker/groups/${id}/`, selected)
  },
  deleteGroups(groupId: number) {
    return axios.delete(`/v1/emissiontracker/groups/${groupId}/`)
  },
  listDataOwnerCategories() {
    return axios.get<MinimalDetailCategory[]>('/v1/emissiontracker/categories/data-owner-categories/')
  },
  updateTargetSetting(companyId: number | string, updateData: HistoricalData) {
    return axios.post(`/v1/emissiontracker/target-setting/${companyId}/update_or_create/`, updateData)
  },
  retrieveTargetSetting(companyId: number | string) {
    return axios.get(`/v1/emissiontracker/target-settings/retrieve/${companyId}/`)
  },
  sourcesWithTypes(companyId: number | string, searchName: string) {
    const sourceSearch = searchName ? `?search=${searchName}` : ''
    return axios.get(`/v1/emissiontracker/sources-with-types/${companyId}/${sourceSearch}`)
  },
  archiveSource(selectedSource: number[]) {
    return axios.put(`/v1/emissiontracker/sources/multiple/archive/`, { ids: selectedSource })
  },

  deleteSource(data: Source) {
    return axios.put(`/v1/emissiontracker/sources/multiple/delete/`, data)
  },
  listSources(companyId: number | string) {
    return axios.get(`/v1/emissiontracker/sources/${companyId}/`)
  },
  createSource(sourceData: SourceData | SourceData[]) {
    return axios.post(`/v1/emissiontracker/types/sources/`, sourceData)
  },

  updateSourceById(id: number, data: SourceUpdatePayload) {
    return axios.put(`/v1/emissiontracker/types/sources/${id}/`, data)
  },

  updateSource(data: Source) {
    return axios.put(`/v1/emissiontracker/sources/multiple/archive/`, data)
  },
  getSourceId(sourceId: number) {
    return axios.get(`/v1/emissiontracker/types/sources/${sourceId}/`)
  },
  activitiesType(typeId: number | string) {
    return axios.get(`/v1/emissiontracker/activities/${typeId}/`)
  },
  activitiesQuestionType(typeId: number | string) {
    return axios.get(`/v1/emissiontracker/activities/questions/${typeId}/`)
  },
  userGuidance(companyId: number | string, guideName: string, userId: number | string) {
    return axios.get(`/v1/emissiontracker/company/${companyId}/guidance/${guideName}/user/${userId}/`)
  },
  createUserGuidance(companyId: number | string, guideName: string) {
    return axios.post(`/v1/emissiontracker/create-guidance/company/${companyId}/`, { has_seen_guidance: true, guidance_name: guideName })
  },

  getSource(companyId: number | string) {
    return axios.get(`/v1/emissiontracker/sources/${companyId}/`)
  },
  listActivities() {
    return axios.get(`/v1/emissiontracker/activities/`)
  },
  saveSource(data: SourceUpdatePayload) {
    return axios.post(`/v1/emissiontracker/types/sources/`, data)
  },
  getSourceTypes(id: number) {
    return axios.get(`/v1/emissiontracker/types/sources/${id}/`)
  },
  scopesSortedCategories() {
    return axios.get('/v1/emissiontracker/scopes-with-sorted-ghg-categories/')
  },
  createActivities(data: ActivitiesPayload) {
    return axios.post(`/v1/emissiontracker/activities/`, data)
  },
  getEmissiontracker(id: number | string) {
    return axios.get(`/v1/emissiontracker/activities/${id}/`)
  },
  createQuestions(data: QuestionsPayload) {
    return axios.post(`/v1/emissiontracker/activities/questions/multiple-create/`, data)
  },
  createCalculators(data: calculatorsPayload) {
    return axios.post(`/v1/emissiontracker/activities/calculators/multiple-create/`, data)
  },
  updateActivity(id: number, data: ActivitiesPayload) {
    return axios.put(`/v1/emissiontracker/activities/${id}/`, data)
  },
  getQuestions(id: number | string) {
    return axios.get(`/v1/emissiontracker/activities/questions/${id}/`)
  },
  getCalculators(id: number | string) {
    return axios.get(`/v1/emissiontracker/activities/calculators/${id}/`)
  },
  deleteCalculator(id: number | string) {
    return axios.delete(`/v1/emissiontracker/activities/calculators/delete/${id}/`)
  },
  deleteQuestion(id: number | string) {
    return axios.delete(`/v1/emissiontracker/activities/questions/delete/${id}/`)
  },
  updateQuestions(data: MultipleQuestions) {
    return axios.put('/v1/emissiontracker/activities/questions/multiple-update/', data)
  },
  updateCalculators(data: Multiplecalculator) {
    return axios.put(`/v1/emissiontracker/activities/calculators/multiple-update/`, data)
  },
})
